<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { GetTransportSourcesQuery } from "@/graphql";
import { orderBy } from "lodash";
import SelectInputWithKeyValue from "@/components/Reusables/Inputs/SelectInputWithKeyValue.vue";
import TransportInputLabel from "@/features/TransportMappingList/components/Inputs/TransportInputLabel.vue";

const props = withDefaults(
  defineProps<{
    options: GetTransportSourcesQuery["transportSource"][0]["transportStandardValues"];
    /** Inert and greyed out, matching `NumberInput`'s disabled look. Set for a Viewer (RTLCA-5581). */
    disabled?: boolean;
  }>(),
  { disabled: false }
);

const emit = defineEmits<{
  (e: "change", standardValueId: string | null): void;
}>();

const selectedMainGroup = ref<string | null>(null);
const selectedStandardValueId = ref<string | null>(null);

const mainGroups = computed(() => {
  return orderBy(
    props.options.reduce(
      (acc, item) => {
        if (!acc.some((group) => group.mainGroup === item.mainGroup)) {
          acc.push({ id: item.id, mainGroup: item.mainGroup });
        }
        return acc;
      },
      [] as { id: string; mainGroup: string }[]
    ),
    ["mainGroup"],
    ["asc"]
  );
});

const subGroups = computed(() => {
  const groups = orderBy(
    props.options.filter((item) => item.mainGroup === selectedMainGroup.value),
    ["subGroup"],
    ["asc"]
  );
  if (groups.length === 1) {
    selectedStandardValueId.value = groups[0].id;
    emit("change", groups[0].id);
  }

  return groups;
});

const mainGroupOptions = computed(() => mainGroups.value.map(({ mainGroup }) => ({ name: mainGroup, value: mainGroup })));

const subGroupOptions = computed(() => subGroups.value.map(({ id, subGroup }) => ({ name: subGroup, value: id })));

const onSelectSubGroup = (subGroupId: string) => {
  selectedStandardValueId.value = subGroupId;
  emit("change", subGroupId);
};

watch(selectedMainGroup, () => {
  emit("change", null);
  selectedStandardValueId.value = null;
});
</script>

<template>
  <div class="col-span-3 col-start-3 flex gap-4">
    <div class="flex flex-1 flex-col">
      <TransportInputLabel primary-text="Main group" />
      <SelectInputWithKeyValue
        v-model:value="selectedMainGroup"
        :options="mainGroupOptions"
        :is-disabled="disabled"
        placeholder="Select main group"
        aria-label="Main group" />
    </div>
    <div class="flex flex-1 flex-col">
      <template v-if="selectedMainGroup">
        <TransportInputLabel primary-text="sub group" />
        <SelectInputWithKeyValue
          :value="selectedStandardValueId"
          :options="subGroupOptions"
          :is-disabled="disabled"
          placeholder="Select sub group"
          aria-label="Sub group"
          @update:value="onSelectSubGroup" />
      </template>
    </div>
  </div>
</template>
